import { tenants, defaultTenantId, type TenantConfig } from "./tenants.config";

/**
 * Parse the current hostname and resolve the tenant id.
 *
 * Rules:
 *  - Strip leading "www.".
 *  - For *.viagify.com, take the first label as tenant id.
 *  - For localhost / unknown hosts, use defaultTenantId.
 *  - If the resolved subdomain is not in the tenant registry, fall back to
 *    defaultTenantId.
 */
export function getTenantId(): string {
  let hostname: string;
  try {
    hostname = window.location.hostname.toLowerCase();
  } catch {
    // SSR or environments without window
    return defaultTenantId;
  }

  // Strip www.
  if (hostname.startsWith("www.")) {
    hostname = hostname.slice(4);
  }

  // Localhost / IP / bare domain → default
  if (
    hostname === "localhost" ||
    hostname === "viagify.com" ||
    hostname.match(/^\d+\.\d+\.\d+\.\d+$/)
  ) {
    return defaultTenantId;
  }

  // *.viagify.com → take first label
  if (hostname.endsWith(".viagify.com")) {
    const subdomain = hostname.replace(".viagify.com", "");
    if (subdomain && tenants[subdomain]) {
      return subdomain;
    }
    return defaultTenantId;
  }

  // Any other host — try hostname as key, fall back to default
  if (tenants[hostname]) {
    return hostname;
  }

  return defaultTenantId;
}

/**
 * Resolve the full TenantConfig for the current hostname.
 * Always returns a valid config (falls back to default tenant).
 */
export function getTenantConfig(): TenantConfig {
  const id = getTenantId();
  return tenants[id] ?? tenants[defaultTenantId];
}
